/**
 * **One affordance's or weapon's play-time state, as a row states it** (`npc-command-menu` 1.2):
 * the command menu's rows and, later, the unit sheet's rows read it to say "charged, recharges on a
 * 5+", "Discharged", "2 of 3 left", "Used this round" or "Destroyed".
 *
 * It mirrors the walls; it never re-derives them. Each field reads exactly what its wall reads:
 * - `recharge`: the frozen target (the affordance record's `recharge`, or the weapon's own) and the
 *   ledger's `discharged` flag, the two reads {@link checkGrantedActionLegality} and
 *   {@link checkWeaponRecharge} take.
 * - `uses`: the record's Limited pool (`limited`, keyed `pool ?? key`) against the per-affordance
 *   ledger when it carries one, else its `round` or `scene` rate (keyed `key`) against the round
 *   ledger or the per-affordance ledger — the reads {@link checkActionAvailable} takes; a weapon's
 *   `Limited {N}` against the per-affordance ledger, as `canUseLimitedWeapon` reads it.
 * - `roundRate` and `usedThisRound`: a `round` rate's allowance, and whether it has nothing left
 *   this round. They read beside a pool's `uses` when the record carries both facts
 *   (`decisions/0202`: Drop Bomb reads "Used this round" and "2 of 3 left" together). A `turn` rate
 *   is left to the once-per-turn ledger the descriptor's `usedUp` already reads.
 * - `destroyed`: {@link isItemDestroyed} for a weapon; for a granted affordance, whether its item is
 *   a destroyed system ({@link destroyedSystemItemIds}, the carried-actions exclusion).
 *
 * `key` is a granted or effect affordance key (`npcf_charge_cataphract:0`) or a weapon instance key.
 * A key naming neither returns `{ destroyed: false }`. A trait a status switches off stays with the
 * sheet: it is not state this helper reads.
 */
import { type Combatant, type EncounterState, findAffordance, isItemDestroyed, resourceCapRawKey, weaponInstanceKey } from "@lancer-vtt/domain";
import type { OverlayEntry, ResourceCap } from "../effects/atoms";
import { destroyedSystemItemIds } from "./carriedActions";
import { affordable } from "../events/gameEvents";

export type FeatureState = {
  /** A Recharge item: its target and whether it is charged now. */
  recharge?: { on: number; charged: boolean };
  /** A counted allowance: a `Limited {N}` pool or an `N/scene` rate (`per: "scene"`), or an `N/round` rate. */
  uses?: { left: number; max: number; per: "scene" | "round" };
  /** A round rate's allowance per round, whether or not `uses` also reports a pool. */
  roundRate?: number;
  /** A round rate with nothing left this round; may sit beside a pool's `uses`. */
  usedThisRound?: boolean;
  destroyed: boolean;
};

export type FeatureStateArgs = {
  combatant: Combatant;
  state: EncounterState;
  /** An affordance key or a weapon instance key; never built here. */
  key: string;
};

export function featureState(args: FeatureStateArgs): FeatureState {
  const { combatant: c, state, key } = args;
  const ledger = c.current.affordanceState;
  // The predicates read the combatant the caller hands in, which may be newer than `state`'s copy.
  const board: EncounterState = state.combatants[c.id] === c ? state : { ...state, combatants: { ...state.combatants, [c.id]: c } };
  const charged = affordable({ kind: "weapon", carrier: c.id, key, spend: "discharge" }, board, undefined);

  const weapon = (c.base.weapons ?? []).find((w) => weaponInstanceKey(w) === key);
  if (weapon) {
    return {
      ...(weapon.recharge ? { recharge: { on: weapon.recharge, charged } } : {}),
      ...(weapon.limited !== undefined ? { uses: { left: Math.max(0, weapon.limited - (ledger?.[key]?.uses ?? 0)), max: weapon.limited, per: "scene" as const } } : {}),
      destroyed: isItemDestroyed(c.current.resources, key),
    };
  }

  const affordance = findAffordance(c.base.affordances, key);
  if (!affordance) return { destroyed: false };
  const { cap, limited } = affordance;
  const roundUsed = state.activation.actionRoundUses[c.id]?.[key] ?? 0;
  const rateUses =
    cap?.grain === "round" ? { left: Math.max(0, cap.max - roundUsed), max: cap.max, per: "round" as const }
    : cap?.grain === "scene" ? { left: Math.max(0, cap.max - (ledger?.[key]?.uses ?? 0)), max: cap.max, per: "scene" as const }
    : undefined;
  const poolUses = limited ? { left: Math.max(0, limited.max - (ledger?.[limited.pool ?? key]?.uses ?? 0)), max: limited.max, per: "scene" as const } : undefined;
  const uses = poolUses ?? rateUses;
  return {
    ...(affordance.recharge !== undefined ? { recharge: { on: affordance.recharge, charged } } : {}),
    ...(uses ? { uses } : {}),
    ...(cap?.grain === "round" ? { roundRate: cap.max } : {}),
    ...(cap?.grain === "round" && !affordable({ kind: "action_use", carrier: c.id, key, grain: "round", max: cap.max }, board, undefined) ? { usedThisRound: true } : {}),
    destroyed: affordance.source.kind === "granted" && destroyedSystemItemIds(c).has(affordance.source.itemId),
  };
}

/**
 * **A feature's clause cap, as its row states it** (`event-engine-costs` D12): the first
 * round-cadence cap (`consumeResourceOnApply`) a triggered or modifier clause of `entry` carries,
 * as `roundRate`, with `usedThisRound` once the carrier's counter has no use left. Aggression reads
 * "1/ROUND", then "1/ROUND · SPENT" after the Berserker first takes damage in a round. `undefined`
 * when the entry carries no round cap. The read is the clause gate's own (`affordable`).
 */
export function clauseCapState(args: { combatant: Combatant; state: EncounterState; entry: OverlayEntry | undefined }): FeatureState | undefined {
  const { combatant: c, state, entry } = args;
  const cap = entry?.effects
    .map((e): ResourceCap | undefined => (e.kind === "triggered" ? e.consumeResourceOnApply : e.kind === "modifier" ? e.mod.consumeResourceOnApply : undefined))
    .find((k) => k !== undefined && (k.per === undefined || k.per === "round"));
  if (cap === undefined) return undefined;
  const board: EncounterState = state.combatants[c.id] === c ? state : { ...state, combatants: { ...state.combatants, [c.id]: c } };
  const open = affordable({ kind: "resource_cap_use", carrier: c.id, key: resourceCapRawKey(cap), max: cap.max }, board, undefined);
  return { roundRate: cap.max, ...(open ? {} : { usedThisRound: true }), destroyed: false };
}
